import React, { useImperativeHandle, forwardRef, useRef, useEffect, useState } from "react";
import { getImageUrl } from "../../../commons/assets_path";
import { OutputDataArray } from "../../../types/types";

type Props = {
  initialContent: string;
  onContentChange?: (content: string) => void;
  outputs: OutputDataArray;
  handleFocus?: () => void;
  handleInput?: (value: string) => void;
  handleBlur?: (value: string) => void;
  handleClick?: () => void;
  name?: string;
  id?: string;
  errorClass?: string;
  type?: "text";
  style?: object;
  testId?: string;
};

export type TagValue = {
  icon_path: string;
  display_name: string;
  test_value: string;
};

export type HandlerType = {
  target: React.RefObject<HTMLDivElement>;
  value: string;
  formatForBackend: string;
  handleInsertTag: (value: TagValue) => void;
  overrideContent: (value: string) => void;
  handleChangeInputWithSeparator: (value: string) => void;
  setValue: React.Dispatch<React.SetStateAction<string>>;
};

const TagInputField = forwardRef<HandlerType, Props>(
  (
    {
      initialContent,
      outputs,
      handleFocus = null,
      handleInput = null,
      handleClick = null,
      onContentChange = null, // NOTE:不要な可能性があるので後に削除するかも
      handleBlur = null,
      name = "",
      id = "",
      errorClass = "",
      type = "text",
      style = {},
      testId = "",
    },
    ref,
  ) => {
    const [value, setValue] = useState<string>("");
    const [isRefReady, setIsRefReady] = useState(false);
    const localRef = useRef<HTMLDivElement>(null);
    const [initFlg, setInitFlg] = useState(true);
    // const contentRef = useRef(initialContent);
    const savedRangeRef = useRef<Range | null>(null);

    useImperativeHandle(ref, () => ({
      target: localRef,
      formatForBackend: value,
      handleInsertTag,
      overrideContent,
      value,
      setValue,
      handleChangeInputWithSeparator,
    }));

    // キャレット位置を保存
    const saveCaretPosition = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        savedRangeRef.current = selection.getRangeAt(0);
      }
    };

    // キャレット位置を復元
    const restoreCaretPosition = () => {
      const selection = window.getSelection();
      if (selection && savedRangeRef.current) {
        selection.removeAllRanges();
        selection.addRange(savedRangeRef.current);
      }
    };

    // キャレットを末尾に設定
    const setCaretToEnd = () => {
      if (!localRef.current) return;
      localRef.current.focus(); // 要素にフォーカスを設定

      const selection = window.getSelection();
      const range = document.createRange();

      // 要素の内容全体を選択し、末尾にキャレットを移動
      range.selectNodeContents(localRef.current);
      range.collapse(false); // falseで末尾にキャレットを設定

      // 選択範囲をクリアし、新しいキャレット位置を適用
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    };

    const handleInputChange = () => {
      if (localRef.current) {
        const content = localRef.current.innerHTML;
        if (onContentChange) {
          onContentChange(content);
        }
        saveCaretPosition();
        setValue(formatForBackend());
        if (handleInput) {
          handleInput(formatForBackend());
        }
      }
    };

    const handleTagClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.className === "removeTag") {
        target.closest(".react-tag-input__tag__outer")?.remove();
        handleInputChange(); // 削除後の内容を更新
      } else {
        // タグがクリックされた場合、キャレットをタグの後ろに移動
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const clickedTag = target.closest(".react-tag-input__tag__outer");
          if (clickedTag) {
            range.setStartAfter(clickedTag);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      }
    };

    // NOTE: BEに送る値に、HTMLエンティティが含まれないようにデコード
    const decodeHTMLEntities = (text: string) => {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = text;

      return textarea.value
        .replace(/<br\s*\/?>/gi, "")
        .replace(/<\/div>/gi, "")
        .replace(/<div>/gi, "");
    };

    // NOTE: BEに送るために{{output}}に変換する
    const formatForBackend = () => {
      if (localRef?.current) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = localRef.current.innerHTML;
        // HTMLの無駄な空白を削除する
        tempDiv.innerHTML = tempDiv.innerHTML.replace(/\s*(<[^>]+>)\s*/g, "$1").trim();

        // タグを探して{{}}形式に変換
        const tags = tempDiv.querySelectorAll(".react-tag-input__tag__outer");
        tags.forEach((tag) => {
          const tagContent = tag.querySelector(".react-tag-input__tag__content");

          if (tagContent instanceof HTMLElement) {
            // タグを{{タグ名}}形式に置換
            const displayName = tagContent.innerText;
            tag.replaceWith(`{{${displayName}}}`);
          }
        });
        // {{}}内の空白や改行を削除
        tempDiv.innerHTML = tempDiv.innerHTML.replace(/{{\s*(.*?)\s*}}/g, (match, p1) => {
          return `{{${p1.trim()}}}`;
        });

        return decodeHTMLEntities(tempDiv.innerHTML);
      } else {
        return "";
      }
    };

    // NOTE: タグHTMLを生成
    const tagFormat = (value: TagValue) => {
      const iconPath = value?.icon_path ? getImageUrl(value.icon_path) : "";
      const iconNode = iconPath ? `<img src="${iconPath}" alt="" width=20 height=20 />` : "";
      const removeIconPath = getImageUrl("images/icons/grayCancel.svg");
      const displayName = value.display_name.replace(/{{(.*?)}}/, "$1");
      const testValue = value?.test_value ? `<p class="react-tag-input__tag__desc">${value.test_value}</p>` : "";
      const htmlString = `
      <span class="react-tag-input__tag__outer" contentEditable="false" style="vertical-align: middle; display: inline-flex; padding: 0 5px 0 5px; margin-left: 3px">
        <div class="react-tag-input__tag" contentEditable="false" style="display: inline-flex; vertical-align: middle;">
          ${iconNode}
          <div class="react-tag-input__tag__content" style="padding: 0;">
            ${displayName}
          </div>
          ${testValue}
          <img src="${removeIconPath}" width=16 height=16 class="removeTag" style="cursor: pointer;" />
        </div>
      </span>
      `;

      return htmlString.trim();
    };

    // 取得した値からタグを入力
    const handleInsertTag = (value: TagValue) => {
      restoreCaretPosition();
      const htmlString = tagFormat(value);
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // contentEditableなdiv要素内にいるか確認
      if (!localRef.current || !range.commonAncestorContainer) return;

      // キャレット位置がcontentEditable内にあるかチェック
      const isInsideContentEditable = localRef.current.contains(range.commonAncestorContainer);
      if (!isInsideContentEditable) return;

      const template = document.createElement("template");
      template.innerHTML = htmlString.replace(/\s*(<[^>]+>)\s*/g, "$1").trim();

      // template.contentEditable = "false";
      const insertNode = template.content.childNodes[0];
      range.insertNode(insertNode);

      range.setStartAfter(insertNode);
      range.collapse(true);

      selection.removeAllRanges();
      selection.addRange(range);

      saveCaretPosition();
      handleInputChange();
    };

    // NOTE: 入力値のreffererがapi-pulldownタイプだった場合用
    const overrideContent = (value: string) => {
      if (localRef.current) {
        setValue(value);
        localRef.current.innerHTML = value;
      }
    };

    const handleChangeInputWithSeparator = (targetValue: string) => {
      if (localRef.current) {
        const result = value ? `${value},${targetValue}` : targetValue;

        setValue(result);
        localRef.current.innerHTML = /{{(.*?)}}/.test(value) ? `${localRef.current.innerHTML},${targetValue}` : result;

        handleInputChange();
        setCaretToEnd();
      }
    };

    // NOTE: propsで受け取った値から取得した値の箇所をタグにリプレイスする
    const replaceTemplateTags = () => {
      if (!initialContent) return "";

      // {{}}を探して置換
      const replacedText = initialContent.replace(/{{(.*?)}}/g, (match, p1) => {
        const output = findOutputByDisplayName(p1);
        return output ? tagFormat(output as TagValue) : match; // 対象外は元のテキストをそのまま保持
      });

      return replacedText;
    };
    // NOTE: タグへのリプレイスに必要な値をoutputsから取得
    const findOutputByDisplayName = (targetDisplayName: string) => {
      for (const item of outputs) {
        const match = item.outputs.find((output) => output.display_name === targetDisplayName);

        if (match) {
          return {
            display_name: match.display_name,
            test_value: match.test_value,
            icon_path: item.icon_path,
          };
        }
      }
      return {
        display_name: targetDisplayName,
      };
    };

    // NOTE: コピー元のフォントをリセットする
    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault(); // デフォルトのペースト動作を無効化

      // クリップボードからプレーンテキストを取得
      const text = e.clipboardData.getData("text/plain");

      // キャレット位置を取得してテキストを挿入
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents(); // 現在の選択範囲を削除
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);

        // キャレットを挿入したテキストの後ろに移動
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);

        if (handleInput) {
          handleInput(formatForBackend());
        }
      }
    };

    const handleTextFormattingShortcuts = (event: React.KeyboardEvent<HTMLDivElement>) => {
      const isBoldShortcut = (event.metaKey && event.key === "b") || (event.ctrlKey && event.key === "b");
      const isItalicShortcut = (event.metaKey && event.key === "i") || (event.ctrlKey && event.key === "i");
      const isUnderlineShortcut = (event.metaKey && event.key === "u") || (event.ctrlKey && event.key === "u");

      if (isBoldShortcut || isItalicShortcut || isUnderlineShortcut) {
        event.preventDefault(); // デフォルトのショートカット処理を無効化
      }
    };

    const escapeSlackMentions = (str: string) => {
      return str.replace(/<!(subteam\^[^>|]+(?:\|[^>]+)?|here|channel|everyone)>/g, (match) => {
        return match.replace("<", "&lt;").replace(">", "&gt;");
      });
    };

    useEffect(() => {
      if (localRef.current && initFlg) {
        setIsRefReady(true); // 初回レンダリング後にrefの設定を確定
        const replacedText = escapeSlackMentions(replaceTemplateTags());

        localRef.current.innerHTML = replacedText;
        setValue(initialContent);

        if (initialContent) {
          setInitFlg(false);
        }
      }
    }, [initialContent, initFlg]);

    return (
      <>
        <input type="hidden" name={name} value={value} id={id} data-testid={`${testId}-value`} />
        <div
          ref={localRef}
          data-testid={`${testId}-field`}
          contentEditable
          className={`addressInput form-control no-scrollbar ${errorClass}`}
          onInput={handleInputChange}
          onClick={handleTagClick}
          onFocus={() => {
            if (handleFocus) {
              handleFocus();
            }
            const element = localRef.current;
            if (element && element.scrollWidth > element.clientWidth) {
              element.scrollLeft = element.scrollWidth;
            }
            restoreCaretPosition();
            setIsRefReady(true);
          }}
          onBlur={(e) => {
            saveCaretPosition();
            setValue(formatForBackend());
            if (handleBlur) {
              handleBlur(formatForBackend());
            }

            if (localRef.current) {
              localRef.current.scrollLeft = -localRef.current.scrollWidth;
            }
          }}
          onKeyDown={(e) => {
            if (!localRef.current) return;
            if (e.key === "Enter" || (e.key === "Enter" && e.shiftKey)) {
              e.preventDefault();
            }
            handleTextFormattingShortcuts(e);
            if (e.key === "Backspace" || e.key === "Delete") {
              // textNode削除&carret位置の右側にタグがある時、最後の一文字の削除だけ無効化して、手動で消す
              // contentEditableのデフォルト機能で無駄なbrが入るため。
              const selection = window.getSelection();

              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const startContainer = range.startContainer; // キャレットがあるノード
                const startOffset = range.startOffset; // キャレットのオフセット

                if (startContainer.nodeType === Node.TEXT_NODE) {
                  const textNode = startContainer;

                  // キャレットがテキストノードの末尾にある場合
                  if (startOffset === textNode.textContent?.length && textNode.textContent.length === 1) {
                    // テキストノードの次の兄弟ノードを取得
                    const parentElement = textNode.parentElement; // テキストノードの親要素
                    const childNodes = parentElement?.childNodes;

                    if (childNodes) {
                      const textNodeIndex = Array.from(childNodes).indexOf(textNode as ChildNode);

                      // テキストノードの次のノードを確認
                      const nextNode = childNodes[textNodeIndex + 1];
                      if (
                        nextNode &&
                        nextNode.nodeType === Node.ELEMENT_NODE &&
                        (nextNode as HTMLElement).classList.contains("react-tag-input__tag__outer")
                      ) {
                        e.preventDefault();
                        textNode.textContent = "";
                      }
                    }
                  }
                }
              }
            }
          }}
          onPaste={handlePaste}
          style={{
            outline: "none",
            boxShadow: "none",
            overflowX: "auto",
            whiteSpace: "nowrap",
            maxHeight: "44px",
            height: "44px",
            lineHeight: "2.2",
            ...style,
          }}
        />
      </>
    );
  },
);

export default TagInputField;
